import { defineStore } from 'pinia'
import { useQuery } from 'villus'
import { ref } from 'vue'
import { useRouter } from 'vue-router'

import { type CheckboxGroupData } from '@/components/layout/CheckboxGroup.type'
import { type ChipData } from '@/components/TagChip.type'
import {
  type TagForDitto,
  type TagGroupForDitto,
  TagGroupsDocument,
} from '@/graphql/creator-platform/generated'
import { backendLocation } from '@/graphql/utils'
import { OptionalQueryString, upsertOrDeleteQueryString } from '@/utils/queryString'

export const useTagStore = defineStore('tagStore', () => {
  const tagGroups = ref<Array<Pick<TagGroupForDitto, 'id' | 'name' | 'tags'>>>()
  const tagList = ref<Map<string, ChipData>>(new Map())
  const checkboxRef = ref<CheckboxGroupData[][]>([])
  const router = useRouter()
  async function init(): Promise<void> {
    const tagGroupQuery = await useQuery({
      query: TagGroupsDocument,
      variables: {
        location: backendLocation,
      },
    })
    const { data, error } = tagGroupQuery
    if (data.value !== null) {
      tagGroups.value = data.value.tagGroupsForDitto
      await initCheckboxRef()
    }
    if (error.value !== null) {
      throw new Error('tagGroups error')
    }
  }
  async function getTagName(tags: TagForDitto[]): Promise<CheckboxGroupData[]> {
    const tagsQs = router.currentRoute.value.query[OptionalQueryString.tags]
    const queryStringTags = typeof tagsQs === 'string' ? tagsQs.split(',') : []
    const checkboxList = tags.map((e) => {
      const isTagInQueryString = queryStringTags.includes(e.id)
      return {
        label: e.name,
        key: e.id,
        isChecked: isTagInQueryString,
      }
    })
    for (const checkbox of checkboxList) {
      if (checkbox.isChecked) {
        await updateTagList(checkbox.key, checkbox.label)
      }
    }
    return checkboxList
  }

  async function initCheckboxRef(): Promise<void> {
    if (tagGroups.value === undefined) return
    for (const tagGroup of tagGroups.value) {
      checkboxRef.value.push(await getTagName(tagGroup.tags))
    }
  }
  async function updateTagList(key: string, label: string): Promise<void> {
    const data = {
      title: label,
      key,
    }
    if (tagList.value.has(key)) {
      tagList.value.delete(key)
    } else {
      tagList.value.set(key, data)
    }
    const queryString = [...tagList.value.values()].map((x) => x.key).toString()
    await upsertOrDeleteQueryString({ tags: queryString }, router)
  }

  async function removeTag(key: string): Promise<void> {
    tagList.value.delete(key)
    for (const array of checkboxRef.value) {
      for (const item of array) {
        if (item.key === key) {
          item.isChecked = false
        }
      }
    }
    const tagKeys = [...tagList.value.values()].map((x) => x.key)
    await upsertOrDeleteQueryString(
      { tags: tagKeys.length === 0 ? null : tagKeys.toString() },
      router,
    )
  }

  async function removeAllTags(): Promise<void> {
    tagList.value.clear()
    for (const array of checkboxRef.value) {
      for (const item of array) {
        item.isChecked = false
      }
    }
    await upsertOrDeleteQueryString({ tags: null }, router)
  }
  return {
    init,
    tagGroup: tagGroups,
    tagList,
    updateTagList,
    checkboxRef,
    removeTag,
    removeAllTags,
  }
})
